import { useEffect, useMemo, useState } from "react";
import { Button, ErrorNote, Panel, Select } from "../../components/ui";
import { BarChart } from "../../components/charts";
import { formatMoney, formatMoneyShort } from "../../lib/money";
import {
  dr15, grossPerDeal, recordBook, trailingMonths, recentMonths,
  monthLabel, monthShort, toCsv, downloadCsv,
  type Dr15, type GrossLine, type RecordBookRow, type MonthTotals, type MonthKey,
} from "../../lib/reports";

type Tab = "summary" | "dr15" | "gross" | "book";

const TABS: { id: Tab; label: string; note: string }[] = [
  { id: "summary", label: "Summary",     note: "Twelve months" },
  { id: "dr15",    label: "DR-15",       note: "Sales tax" },
  { id: "gross",   label: "Gross",       note: "What each car made" },
  { id: "book",    label: "Record book", note: "Every vehicle" },
];

export default function Reports() {
  const [tab, setTab] = useState<Tab>("summary");
  const months = useMemo(() => recentMonths(24), []);
  const [month, setMonth] = useState<MonthKey>(months[0]!);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const [summary, setSummary] = useState<MonthTotals[]>([]);
  const [tax, setTax] = useState<Dr15 | null>(null);
  const [gross, setGross] = useState<GrossLine[]>([]);
  const [book, setBook] = useState<RecordBookRow[]>([]);

  useEffect(() => {
    let live = true;
    setBusy(true); setErr(null);
    const work =
      tab === "summary" ? trailingMonths(12).then((r) => live && setSummary(r))
      : tab === "dr15"  ? dr15(month).then((r) => live && setTax(r))
      : tab === "gross" ? grossPerDeal(month).then((r) => live && setGross(r))
      :                   recordBook().then((r) => live && setBook(r));
    work
      .catch((e) => live && setErr(e instanceof Error ? e.message : "Could not build the report."))
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [tab, month]);

  const picker = (
    <Select label="Month" value={`${month.year}-${month.month}`}
            onChange={(v) => {
              const [y, m] = v.split("-").map(Number);
              setMonth({ year: y!, month: m! });
            }}>
      {months.map((m) => (
        <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
          {monthLabel(m)}
        </option>
      ))}
    </Select>
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Reports</h2>
          <p className="pagehead-sub">
            Every figure is read from the deal as it was signed. Nothing here
            recalculates tax.
          </p>
        </div>
      </div>

      <nav className="tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id}
                  className={`tab ${tab === t.id ? "on" : ""}`}
                  onClick={() => setTab(t.id)}>
            <span className="tab-l">{t.label}</span>
            <span className="tab-n">{t.note}</span>
          </button>
        ))}
      </nav>

      {err && <ErrorNote>{err}</ErrorNote>}
      {busy && <div className="skeleton-block" aria-label="Loading" />}

      {!busy && tab === "summary" && <Summary rows={summary} />}
      {!busy && tab === "dr15"    && <Dr15View data={tax} picker={picker} />}
      {!busy && tab === "gross"   && <GrossView rows={gross} picker={picker} month={month} />}
      {!busy && tab === "book"    && <BookView rows={book} />}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Summary({ rows }: { rows: MonthTotals[] }) {
  const units = rows.reduce((n, r) => n + r.units, 0);
  const sales = rows.reduce((n, r) => n + r.grossSalesCents, 0);
  const invoiced = rows.reduce((n, r) => n + r.invoicedCents, 0);
  const profit = rows.reduce((n, r) => n + r.profitCents, 0);
  const tax = rows.reduce((n, r) => n + r.taxDueCents, 0);
  const best = rows.reduce<MonthTotals | null>(
    (b, r) => (!b || r.profitCents > b.profitCents ? r : b), null);

  if (!units) {
    return <Panel title="Twelve months" rail="record">
      <p className="hint">No sold deals yet. This fills in as you sell.</p>
    </Panel>;
  }

  return (
    <>
      <div className="statgrid">
        <Stat label="Sold" value={String(units)} note="last 12 months" />
        <Stat label="Sales" value={formatMoneyShort(sales)} note="vehicles only" />
        {/* What was actually billed — price, fees and tax together. "Sales"
            is the cars alone, which is the smaller number and the one that
            never matches the bank. */}
        <Stat label="Invoiced" value={formatMoneyShort(invoiced)}
              note="with fees and tax" />
        <Stat label="Gross profit" value={formatMoneyShort(profit)}
              note={units ? `${formatMoneyShort(Math.round(profit / units))} a car` : ""}
              tone={profit < 0 ? "down" : "up"} />
        <Stat label="Tax collected" value={formatMoneyShort(tax)} note="state and surtax" />
      </div>

      <Panel title="Gross profit by month" rail="money"
             figure={best ? `best ${monthLabel(best.month)}` : undefined}>
        <BarChart tone="ok" data={rows.map((r) => ({
          label: monthShort(r.month), value: Math.max(0, r.profitCents),
        }))} />
      </Panel>

      <Panel title="Invoiced by month" rail="money"
             figure={formatMoney(invoiced)}>
        <BarChart tone="sky" data={rows.map((r) => ({
          label: monthShort(r.month), value: r.invoicedCents,
        }))} />
      </Panel>

      <Panel title="Units by month" rail="record">
        <BarChart tone="sky" money={false}
                  data={rows.map((r) => ({ label: monthShort(r.month), value: r.units }))} />
      </Panel>
    </>
  );
}

function Stat({ label, value, note, tone }: {
  label: string; value: string; note?: string; tone?: "up" | "down";
}) {
  return (
    <div className="stat">
      <span className="stat-l">{label}</span>
      <span className={`stat-v ${tone ? `stat-${tone}` : ""}`}>{value}</span>
      {note && <span className="stat-n">{note}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Dr15View({ data, picker }: { data: Dr15 | null; picker: React.ReactNode }) {
  if (!data) return null;
  const { lines } = data;

  function exportCsv() {
    downloadCsv(
      `dr15-${data!.month.year}-${String(data!.month.month).padStart(2, "0")}.csv`,
      toCsv(
        ["Deal", "Date", "Buyer", "Vehicle", "County",
         "Gross sale", "Taxable", "State tax", "Surtax"],
        lines.map((l) => [
          l.dealNo, l.date, l.buyer, l.vehicle, l.county,
          (l.grossCents / 100).toFixed(2), (l.taxableCents / 100).toFixed(2),
          (l.stateTaxCents / 100).toFixed(2), (l.surtaxCents / 100).toFixed(2),
        ])));
  }

  return (
    <>
      <div className="reportbar">{picker}</div>

      <Panel title={`Sales tax — ${monthLabel(data.month)}`} rail="money"
             figure={formatMoney(data.taxDueCents)}
             foot={lines.length > 0 &&
               <Button variant="ghost" onClick={exportCsv}>Export CSV</Button>}>
        {lines.length === 0 ? (
          <p className="hint">No sold deals in {monthLabel(data.month)}.</p>
        ) : (
          <>
            <dl className="spec">
              <div><dt>Gross sales</dt><dd>{formatMoney(data.grossCents)}</dd></div>
              <div><dt>Exempt (trade allowances)</dt><dd>{formatMoney(data.exemptCents)}</dd></div>
              <div><dt>Taxable amount</dt><dd>{formatMoney(data.taxableCents)}</dd></div>
              <div><dt>State tax at 6%</dt><dd>{formatMoney(data.stateTaxCents)}</dd></div>
              <div><dt>Discretionary surtax</dt><dd>{formatMoney(data.surtaxCents)}</dd></div>
              <div className="spec-total">
                <dt>Tax due</dt><dd>{formatMoney(data.taxDueCents)}</dd></div>
            </dl>
            <p className="taxnote">
              {lines.length} sold {lines.length === 1 ? "deal" : "deals"}, counted
              by the date on the contract. Surtax is charged on the first $5,000
              of each vehicle at the county rate recorded when the deal was
              signed, so a later rate change does not move these figures.
            </p>
          </>
        )}
      </Panel>

      {data.byCounty.length > 1 && (
        <Panel title="By county" rail="record"
               note="the DR-15 asks for surtax split this way">
          <div className="item">
            {data.byCounty.map((c) => (
              <div className="li" key={c.county}>
                <span>{c.county}<span className="dim"> — {formatMoney(c.taxableCents)} taxable</span></span>
                <span className="money">{formatMoney(c.surtaxCents)}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {lines.length > 0 && (
        <Panel title="The deals behind it" rail="record" note={`${lines.length}`}>
          <div className="tablewrap">
            <table className="dealtable">
              <thead><tr>
                <th>Deal</th><th>Date</th><th>Buyer</th><th>County</th>
                <th className="num">Taxable</th><th className="num">State</th>
                <th className="num">Surtax</th>
              </tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.dealNo}>
                    <td className="mono dim">{l.dealNo}</td>
                    <td>{l.date}</td>
                    <td className="strong">{l.buyer}</td>
                    <td>{l.county}</td>
                    <td className="num">{formatMoney(l.taxableCents)}</td>
                    <td className="num">{formatMoney(l.stateTaxCents)}</td>
                    <td className="num">{formatMoney(l.surtaxCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function GrossView({ rows, picker, month }: {
  rows: GrossLine[]; picker: React.ReactNode; month: MonthKey;
}) {
  const total = rows.reduce((n, r) => n + r.grossCents, 0);
  const avgDays = rows.length
    ? Math.round(rows.reduce((n, r) => n + (r.daysHeld ?? 0), 0) / rows.length)
    : 0;

  function exportCsv() {
    downloadCsv(`gross-${month.year}-${String(month.month).padStart(2, "0")}.csv`,
      toCsv(["Deal", "Date", "Stock", "Vehicle", "Sold for", "Cost", "Gross", "Days held"],
        rows.map((r) => [
          r.dealNo, r.date, r.stockNo, r.vehicle,
          (r.soldCents / 100).toFixed(2), (r.costCents / 100).toFixed(2),
          (r.grossCents / 100).toFixed(2), r.daysHeld ?? "",
        ])));
  }

  return (
    <>
      <div className="reportbar">{picker}</div>

      {rows.length === 0 ? (
        <Panel title={`Gross — ${monthLabel(month)}`} rail="money">
          <p className="hint">No sold deals in {monthLabel(month)}.</p>
        </Panel>
      ) : (
        <>
          <div className="statgrid">
            <Stat label="Sold" value={String(rows.length)} />
            <Stat label="Gross profit" value={formatMoney(total)}
                  tone={total < 0 ? "down" : "up"} />
            <Stat label="Average a car"
                  value={formatMoney(Math.round(total / rows.length))} />
            <Stat label="Average days held" value={String(avgDays)} note="purchase to contract" />
          </div>

          <Panel title={`Every car sold in ${monthLabel(month)}`} rail="money"
                 figure={formatMoney(total)}
                 foot={<Button variant="ghost" onClick={exportCsv}>Export CSV</Button>}>
            <div className="tablewrap">
              <table className="dealtable">
                <thead><tr>
                  <th>Stock</th><th>Vehicle</th>
                  <th className="num">Sold</th><th className="num">Cost</th>
                  <th className="num">Gross</th><th className="num">Days</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.dealNo}>
                      <td className="mono dim">{r.stockNo}</td>
                      <td className="strong">{r.vehicle}</td>
                      <td className="num">{formatMoneyShort(r.soldCents)}</td>
                      <td className="num">{formatMoneyShort(r.costCents)}</td>
                      <td className={`num gross ${r.grossCents < 0 ? "negative" : ""}`}>
                        {formatMoneyShort(r.grossCents)}
                      </td>
                      <td className="num">{r.daysHeld ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="taxnote">
              Cost is the car's total cost, which includes every expense line —
              transport, parts, paint. It is not the purchase price alone.
            </p>
          </Panel>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function BookView({ rows }: { rows: RecordBookRow[] }) {
  const sold = rows.filter((r) => r.soldOn).length;

  function exportCsv() {
    downloadCsv(`sales-record-book-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(["Stock", "VIN", "Vehicle", "Purchased", "Purchased from",
             "Sold", "Sold to", "Deal", "Title"],
        rows.map((r) => [
          r.stockNo, r.vin, r.vehicle, r.purchasedOn, r.purchasedFrom,
          r.soldOn ?? "", r.soldTo ?? "", r.dealNo ?? "", r.titleStatus,
        ])));
  }

  return (
    <Panel title="Sales record book" rail="record"
           figure={`${rows.length} vehicles`}
           foot={rows.length > 0 &&
             <Button variant="ghost" onClick={exportCsv}>Export CSV</Button>}>
      {rows.length === 0 ? (
        <p className="hint">No vehicles yet.</p>
      ) : (
        <>
          <p className="taxnote">
            Every vehicle bought, and who it was sold to. {sold} of {rows.length}
            {sold === 1 ? " has" : " have"} sold. Florida requires a dealer keep
            this record; export it and keep a copy off this system.
          </p>
          <div className="tablewrap">
            <table className="dealtable">
              <thead><tr>
                <th>Stock</th><th>VIN</th><th>Vehicle</th>
                <th>Bought</th><th>From</th><th>Sold</th><th>To</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.stockNo}>
                    <td className="mono dim">{r.stockNo}</td>
                    <td className="mono dim">{r.vin}</td>
                    <td className="strong">{r.vehicle}</td>
                    <td>{r.purchasedOn}</td>
                    <td>{r.purchasedFrom}</td>
                    <td>{r.soldOn ?? <span className="dim">on the lot</span>}</td>
                    <td>{r.soldTo ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
